/**
 * API environment configuration
 * Unified API base URL management for all apps
 */

/**
 * Environment types
 */
export type ApiEnvironment = 'local' | 'dev' | 'development' | 'test' | 'production';

/**
 * API environment URLs mapping
 */
export const apiEnvs: Record<ApiEnvironment | string, string> = {
  local: 'http://127.0.0.1:3000',
  dev: 'https://api-test.veeso.ai',
  development: 'https://api-test.veeso.ai',
  test: 'https://api-test.veeso.ai',
  production: 'https://api.veeso.ai',
};

/**
 * agent API base URL mapping
 */
export const agentEnvs: Record<ApiEnvironment | string, string> = {
  local: 'http://localhost:9001',
  dev: 'https://agent-test.veeso.ai',
  development: 'https://agent-test.veeso.ai',
  test: 'https://agent-test.veeso.ai',
  production: 'https://agent.veeso.ai',
};

/**
 * Static assets base URL mapping
 */
export const staticEnvs: Record<ApiEnvironment | string, string> = {
  local: 'https://static.veeso.ai',
  dev: 'https://static.veeso.ai',
  development: 'https://static.veeso.ai',
  test: 'https://static.veeso.ai',
  production: 'https://static.veeso.ai',
};

/**
 * Get API base URL for the given environment
 * @param env - Environment name (e.g., 'production', 'development', 'local')
 * @returns API base URL
 */
export function getApiBaseUrl(env?: string): string {
  const environment = env || 'production';
  return (apiEnvs[environment] ?? apiEnvs.production)!;
}

/**
 * Get Agent API base URL for the given environment
 * @param env - Environment name (e.g., 'production', 'development', 'local')
 * @returns Agent API base URL
 */
export function getAgentApiBaseUrl(env?: string): string {
  const environment = env || 'production';
  return (agentEnvs[environment] ?? agentEnvs.production)!;
}

/**
 * Get static base URL for the given environment
 * @param env - Environment name
 * @returns Static base URL
 */
export function getStaticBaseUrl(env?: string): string {
  const environment = env || 'production';
  return (staticEnvs[environment] ?? staticEnvs.production)!;
}

/**
 * Resolve environment from various sources
 * Works in both browser (Vite) and Node.js environments
 */
export function resolveEnvironment(): string {
  // veeso environment
  if (process.env.VEESO_ENV) {
    return process.env.VEESO_ENV;
  }
  // Node.js environment
  if (process.env.NODE_ENV) {
    return process.env.NODE_ENV;
  }

  // Vite environment (browser)
  if (typeof import.meta !== 'undefined' && (import.meta as any).env?.MODE) {
    return (import.meta as any).env.MODE;
  }

  return 'production';
}

/**
 * Get API base URL for current environment
 * Automatically detects environment from Vite or Node.js
 * @returns API base URL for current environment
 */
export function getApiBaseUrlAuto(): string {
  return getApiBaseUrl(resolveEnvironment());
}

/**
 * Get static base URL for current environment
 * Automatically detects environment from Vite or Node.js
 * @returns Static base URL for current environment
 */
export function getStaticBaseUrlAuto(): string {
  return getStaticBaseUrl(resolveEnvironment());
}
